import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { AiOutlineEdit } from 'react-icons/ai';
import { IoEyeOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import LoadingButton from '../../components/LoadingButton';
import PageHeader from '../../components/PageHeader';
import { LoadingState } from '../../utilities/constants';
import { fetchAssets } from './assetsSlice';
import DataTable from '../../components/DataTable';
import { useTranslation } from 'react-i18next';
import { exportCsv, formatArrayValueToString } from '../../utilities/common';
import { fetchEmissionProfilesTree } from '../emission-profile/emissionProfileSlice';
import NodeIcon from '../../components/NodeIcon';
import { recursiveMap } from '../../utilities/treeHelpers';
import TreeViewModal from '../../components/TreeViewModal';
import { assignAssetsApi } from '../emission-profile/emissionProfileApi';
import { toast } from 'react-toastify';

const ManageAssets = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const match = useRouteMatch();
  const assetStatus = useSelector((state) => state.assets.status);
  const assets = useSelector((state) => state.assets.assets);
  const orgId = useSelector((state) => state.users.user.usrOrgId);
  const emissionProfileStatus = useSelector((state) => state.emissionProfiles.status);
  const [showEPSelector, setShowEPSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const emissionProfileTree = useSelector((state) => state.emissionProfiles.tree);
  useEffect(() => {
    if (emissionProfileStatus === LoadingState.idle) {
      dispatch(fetchEmissionProfilesTree({ orgId }));
    }
  }, [orgId, emissionProfileStatus, dispatch]);

  const onSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedIds(assets.map((d) => d.assetId));
    } else {
      setSelectedIds([]);
    }
  }, [assets]);

  const handleAssignProfile = useCallback(async (eps) => {
    try {
      setIsLoading(true);
      setShowEPSelector(false);
      await assignAssetsApi(orgId, eps.eprflsrcId, { type: 'ADD', divIds: selectedIds });
      setSelectedIds([]);
      dispatch(fetchAssets({ orgId }));
      toast.success(t('The selected assets have been assigned to {{epsName}} successfully', { epsName: eps.eprflsrcName }));
    } catch (error) {
      toast.success(t('Unable to assign the selected assets to {{epsName}}. Please try again.', { epsName: eps.eprflsrcName }));
    } finally {
      setIsLoading(false);
    }
  }, [orgId, dispatch, selectedIds, t]);

  const onSelectRow = useCallback((id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    }
  }, []);

  const transformedTree = useMemo(() => recursiveMap(emissionProfileTree ?? {}, (n) => {
    if (n.eprflId) {
      return {
        weight: n.weight,
        value: n.eprflId,
        labelRaw: n.eprflName,
        label: <span>{n.eprflName}</span>,
        isActive: false
      };
    }
    if (n.eprflcatId) {
      return {
        weight: n.weight,
        value: n.eprflcatId,
        labelRaw: n.eprflcatName,
        label: (
          <>
            <NodeIcon node={n} /> {n.eprflcatName}
          </>
        ),
        isActive: false
      };
    }
    if (n.eprflsrcId) {
      return {
        weight: n.weight,
        value: n.eprflsrcId,
        labelRaw: n.eprflsrcName,
        label: (
          <Button
            as="a"
            variant="link"
            className="text-reset p-0"
            onClick={() => {
              handleAssignProfile(n);
            }}>
            <NodeIcon node={n} />
            {n.eprflsrcName}
          </Button>
        ),
        isActive: false,
        valueId: n.eprflsrcAssignedId
      };
    }
  }), [emissionProfileTree, handleAssignProfile]);
  transformedTree.isRoot = true;

  const tableColumns = useMemo(
    () => [
      {
        id: "select",
        Header: (
          <Form.Check
            type="checkbox"
            onChange={(e) => onSelectAll(e.target.checked)}
          />
        ),
        Cell: ({ row }) => (
          <Form.Check
            type="checkbox"
            checked={selectedIds.includes(row.original.assetId)}
            onChange={(e) =>
              onSelectRow(row.original.assetId, e.target.checked)
            }
            onClick={(e) => e.stopPropagation()}
          />
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
        width: 30,
      },
      {
        Header: t('Asset ID'),
        accessor: 'assetAssignedId'
      },
      {
        Header: t('Asset Name'),
        accessor: 'assetName',
        sortType: 'string'
      },
      {
        Header: t('Assigned Emission Profiles'),
        accessor: (row) => (row.assignedProfiles || []).join(', '),
        Cell: ({ row }) => formatArrayValueToString(t, row.original.assignedProfiles || [], 1),
        sortType: 'string'
      },
      {
        Header: t('Assigned Entities'),
        accessor: (row) => (row.assignedEntities || []).join(', '),
        Cell: ({ row }) => formatArrayValueToString(t, row.original.assignedEntities || [], 1),
        sortType: 'string'
      },
      {
        Header: t('Status'),
        accessor: (row) => {
          return row.assetEnabled === 'Y' ? 'Active' : 'Inactive';
        }
      },
      {
        Header: '',
        accessor: 'actions',
        actionColumn: true,
        disableGlobalFilter: false,
        disableSortBy: true,
        Cell: ({ row }) => {
          const { original } = row;
          return (
            <div className="action-buttons">
              <Button
                to={`${match.url}/${original.assetId}`}
                size="sm"
                className="mr-1"
                variant="outline-secondary"
                as={Link}
              >
                <IoEyeOutline />
              </Button>
              <Button
                to={`${match.url}/${original.assetId}/edit`}
                size="sm"
                className="mr-1"
                variant="outline-secondary"
                as={Link}
              >
                <AiOutlineEdit />
              </Button>
            </div>
          );
        }
      }
    ],
    [match.url, onSelectAll, onSelectRow, selectedIds, t]
  );

  const downloadAssetsHandler = () => {
    const rows = assets.map((item, idx) => ([
      item.assetAssignedId,
      item.assetName,
      item.assetDesc,
      `"${(item.assignedProfiles || []).join(',').replace(/"/g, '')}"`,
      `"${(item.assignedEntities || []).join(',').replace(/"/g, '')}"`,
      item.assetEnabled === 'Y' ? 'Active' : 'Inactive',
    ])).sort((x, y) => x[0] > y[0] ? 1 : -1);
    rows.unshift([
      t('Asset ID'),
      t('Asset Name'),
      t('Description'),
      t('Assigned Emission Profiles'),
      t('Assigned Entities'),
      t('Status'),
    ]);
    exportCsv(rows, 'organization-assets.csv');
  };

  useEffect(() => {
    dispatch(fetchAssets({ orgId }));
  }, [orgId, dispatch]);

  return (
    <Container fluid>
      <Row>
        <Col>
          <PageHeader
            title={t('Manage Assets')}
            actions={
              <>
                <Button to={`${match.url}/new`} as={Link} className="mr-2">
                  + {t('Create Asset')}
                </Button>
                <Button onClick={downloadAssetsHandler} variant="secondary">
                  <FiDownload /> {t('Download')}
                </Button>
              </>
            }
          />
        </Col>
      </Row>
      {assetStatus === LoadingState.loading && <Spinner animation="grow" />}
      {assetStatus === LoadingState.succeeded && (
        <Row>
          <Col>
            <DataTable
              columns={tableColumns}
              data={assets}
              defaultSort="assetCreatedDate"
              headerActions={
                <>
                <LoadingButton isLoading={isLoading} />
                <Button
                  onClick={() => setShowEPSelector(true)}
                  variant="secondary"
                  disabled={!selectedIds.length || isLoading}
                >
                  {t('Assign Emission Profile Source')}
                </Button>
                </>
              }
            />
          </Col>
        </Row>
      )}
      <TreeViewModal
        tree={transformedTree}
        closeModal={() => setShowEPSelector(false)}
        show={showEPSelector}
        title={t('Select Emission Profile')}
      />
      {assetStatus === LoadingState.failed && <div>Something Went Wrong</div>}
    </Container>
  );
};

export default ManageAssets;
